import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { PalmtreeIcon, Plus, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function HolidaysPage() {
  const { user } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const isAdmin = user?.role === "admin";
  const load = async () => {
    try { const { data } = await api.get("/holidays"); setHolidays(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    try { await api.delete(`/holidays/${id}`); toast.success("Holiday removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Holiday calendar</h1>
          <p className="mt-1 text-sm text-zinc-500">{holidays.length} holidays scheduled this year.</p>
        </div>
        {isAdmin && (
            <div className="flex items-center gap-2">
              <SeedCalendarButton onDone={load} />
              <AddHolidayDialog onDone={load} />
            </div>
          )}
      </div>

      <Card className="border-zinc-200 shadow-none">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Date</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Day</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Holiday</TableHead>
                {isAdmin && <TableHead className="text-xs uppercase tracking-wider text-zinc-500 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {holidays.map((h) => {
                const past = dayjs(h.date).isBefore(dayjs(), "day");
                return (
                  <TableRow key={h.id} className={`border-b border-zinc-100 hover:bg-zinc-50/60 ${past ? "opacity-60" : ""}`}>
                    <TableCell className="text-sm font-medium">{dayjs(h.date).format("D MMM YYYY")}</TableCell>
                    <TableCell className="text-sm text-zinc-600">{dayjs(h.date).format("dddd")}</TableCell>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-2"><PalmtreeIcon className="h-4 w-4 text-emerald-600" strokeWidth={1.6} />{h.name}</div>
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => del(h.id)} className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-holiday-${h.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

function AddHolidayDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: dayjs().format("YYYY-MM-DD"), name: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/holidays", form); toast.success("Holiday added"); setOpen(false); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-holiday-btn"><Plus className="h-4 w-4 mr-2" /> Add holiday</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display text-xl">Add holiday</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9" required /></div>
          <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9" required data-testid="input-holiday-name" /></div>
          <DialogFooter><Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">Add</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// Loads a published holiday calendar in one click. The dates live on the
// backend, which refuses to write unless every one of them falls on the
// weekday the calendar claims - a wrong date here is silent and reaches
// payroll, so it is checked rather than trusted.
function SeedCalendarButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [years, setYears] = useState([]);

  useEffect(() => {
    api.get("/holidays/calendars")
      .then((r) => setYears(r.data?.years || []))
      .catch(() => setYears([]));
  }, []);

  const thisYear = dayjs().year();
  const year = years.includes(thisYear) ? thisYear : years[years.length - 1];
  if (!year) return null;

  const run = async () => {
    const okToGo = window.confirm(
      `Load the published ${year} holiday calendar?\n\n` +
      "This adds any dates that are missing and corrects names. " +
      "It never deletes a holiday you added yourself."
    );
    if (!okToGo) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/holidays/seed/${year}`);
      const bits = [];
      if (data.added.length) bits.push(`${data.added.length} added`);
      if (data.renamed.length) bits.push(`${data.renamed.length} corrected`);
      if (!bits.length) bits.push("already up to date");
      toast.success(`${year}: ${bits.join(", ")} — ${data.total} on file`);
      if (data.not_in_calendar?.length) {
        toast(`Kept ${data.not_in_calendar.length} holiday(s) not in the published list: ${data.not_in_calendar.join(", ")}`);
      }
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button variant="outline" onClick={run} disabled={busy}
            data-testid="seed-calendar-btn">
      <PalmtreeIcon className="h-4 w-4 mr-2" />
      {busy ? "Loading…" : `Load ${year} calendar`}
    </Button>
  );
}
